import { Flex, Text } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { useCommunityConfig } from '../../CommunityConfig'
import { useMentionData } from '../context/MentionContext'

import { MentionDeepLinkButton } from './MentionDeepLinkButton'

import type { JSX, ReactNode } from 'react'

export const MentionPrefix_Channel = graphql(/* GraphQL */ `
  fragment MentionPrefix_Channel on Channel @_unmask {
    service
  }
`)

export const MentionPrefix_Mention = graphql(/* GraphQL */ `
  fragment MentionPrefix_Mention on Mention @_unmask {
    mentionType
    publicUrl
  }
`)

export const MentionPrefixView = ({
  label,
  children,
}: {
  label?: string
  children?: ReactNode
}): JSX.Element => (
  <Flex align="center" gap="sm" wrap="wrap">
    {label && (
      <Text size="sm" color="subtle">
        Mentioned you in their {label}
      </Text>
    )}
    {children}
  </Flex>
)

type MentionPrefixProps = {
  channel: FragmentOf<typeof MentionPrefix_Channel>
  /**
   * The card's expanded state. The deep-link button only renders when
   * the card is expanded — collapsed cards stay minimal across all
   * partial-experience variants (notify-only and partial-post).
   */
  expanded: boolean
}

/**
 * Contextual "Mentioned you in their {label}" line beneath the author
 * header, plus an optional deep-link button for partial / notify-only
 * channels. Capabilities decide both pieces:
 *
 * - **Text** is omitted when the channel's capability returns
 *   `showPrefix: false`.
 * - **Deep-link button** appears for partial posts (no inner miniature,
 *   e.g. YouTube — affordance to open the source natively) and
 *   notify-only mentions (e.g. Instagram / Facebook — affordance to
 *   reply natively). Gated on `expanded` in both cases: collapsed
 *   cards show only the prefix text.
 *
 * Returns `null` when neither piece would render.
 *
 * @see `MentionSubjectLabel` in `../../CommunityConfig/mentionsCapabilities`
 *   for the label values.
 */
export const MentionPrefix = ({
  channel,
  expanded,
}: MentionPrefixProps): JSX.Element | null => {
  const mention = useMentionData()
  const { config } = useCommunityConfig()
  const { mentions } = config

  const showText = mentions.showPrefix(channel)

  const isPostKind = mention.mentionType === 'post'
  const canReply = mentions.canReply(channel, mention.mentionType)
  const hasFullContext = mentions.hasFullContext(channel, mention.mentionType)
  const isPartialPost = isPostKind && !hasFullContext
  const isNotifyOnlyComment = !isPostKind && !canReply
  const showDeepLinkButton = (isPartialPost || isNotifyOnlyComment) && expanded

  if (!showText && !showDeepLinkButton) {
    return null
  }

  const label = mentions.subjectLabel(channel, mention.mentionType)

  return (
    <MentionPrefixView label={showText ? label : undefined}>
      {showDeepLinkButton && (
        <MentionDeepLinkButton
          href={mention.publicUrl}
          label={mentions.deepLinkLabel(channel)}
        />
      )}
    </MentionPrefixView>
  )
}

MentionPrefix.displayName = 'MentionPrefix'
